import { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { listPosts, updatePost, getJobStatus, cancelJob, type PostWithRelations, type JobStatusResponse } from '../lib/api';
import SkeletonPostCard from '../components/SkeletonPostCard';

type KanbanColumn = 'draft' | 'in_review' | 'approved';

interface ColumnData {
  id: KanbanColumn;
  title: string;
  posts: PostWithRelations[];
}

interface ActiveJob {
  jobId: string;
  status: JobStatusResponse;
}

const POLLING_INTERVAL = 3000; // 3 seconds
const STORAGE_KEY = 'growe_active_jobs';
const VIEWED_POSTS_KEY = 'growe_viewed_posts';

/**
 * Approve Board - Kanban view for content approval workflow
 * Columns: Draft → In Review → Approved
 * Supports async job tracking with real-time progress updates
 */
export default function ApproveBoard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { workspaceSlug } = useParams<{ workspaceSlug: string }>();
  const [columns, setColumns] = useState<ColumnData[]>([
    { id: 'draft', title: 'Draft', posts: [] },
    { id: 'in_review', title: 'In Review', posts: [] },
    { id: 'approved', title: 'Approved', posts: [] },
  ]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPost, setSelectedPost] = useState<PostWithRelations | null>(null);
  const [activeJobs, setActiveJobs] = useState<ActiveJob[]>([]);
  const [viewedPosts, setViewedPosts] = useState<Set<string>>(new Set());

  // Helper functions for localStorage
  const updateStoredJobs = (jobIds: string[]) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(jobIds));
  };

  const updateViewedPosts = (postIds: Set<string>) => {
    localStorage.setItem(VIEWED_POSTS_KEY, JSON.stringify([...postIds]));
  };

  const markPostAsViewed = (postId: string) => {
    const updated = new Set(viewedPosts);
    updated.add(postId);
    setViewedPosts(updated);
    updateViewedPosts(updated);
  };

  // Initialize active jobs from location state and localStorage
  useEffect(() => {
    const jobIdsFromState = (location.state?.activeJobIds || []) as string[];
    const storedJobs = localStorage.getItem(STORAGE_KEY);
    const storedJobIds = storedJobs ? JSON.parse(storedJobs) : [];

    // Combine and deduplicate
    const allJobIds = [...new Set([...jobIdsFromState, ...storedJobIds])];

    if (allJobIds.length > 0) {
      // Fetch initial status for all jobs
      Promise.all(allJobIds.map(async (jobId) => {
        try {
          const status = await getJobStatus(jobId);
          return { jobId, status };
        } catch (error) {
          console.error(`Failed to fetch job ${jobId}:`, error);
          return null;
        }
      })).then((jobs) => {
        const validJobs = jobs.filter((j): j is ActiveJob => j !== null && j.status.status !== 'completed' && j.status.status !== 'failed');
        setActiveJobs(validJobs);
        updateStoredJobs(validJobs.map(j => j.jobId));
      });
    }

    // Load viewed posts from localStorage
    const storedViewed = localStorage.getItem(VIEWED_POSTS_KEY);
    if (storedViewed) {
      setViewedPosts(new Set(JSON.parse(storedViewed)));
    }
  }, [location]);

  // Poll active jobs
  useEffect(() => {
    if (activeJobs.length === 0) return;

    const interval = setInterval(async () => {
      const updatedJobs = await Promise.all(
        activeJobs.map(async ({ jobId }) => {
          try {
            const status = await getJobStatus(jobId);
            return { jobId, status };
          } catch (error) {
            console.error(`Failed to poll job ${jobId}:`, error);
            return null;
          }
        })
      );

      const validJobs = updatedJobs.filter((j): j is ActiveJob => j !== null);

      // Check for completed jobs
      const completedJobs = validJobs.filter(j => j.status.status === 'completed');
      const failedJobs = validJobs.filter(j => j.status.status === 'failed');
      const stillActive = validJobs.filter(j => j.status.status !== 'completed' && j.status.status !== 'failed');

      // Handle completed jobs
      for (const job of completedJobs) {
        if (job.status.resultPostId) {
          toast.success(t('approve.jobCompleted', { defaultValue: 'Content generated successfully!' }));
          // Reload posts to show the new one
          loadPosts();
        }
      }

      // Handle failed jobs
      for (const job of failedJobs) {
        toast.error(t('approve.jobFailed', { defaultValue: `Generation failed: ${job.status.errorMessage || 'Unknown error'}` }));
      }

      // Update active jobs
      setActiveJobs(stillActive);
      updateStoredJobs(stillActive.map(j => j.jobId));

      // If all jobs completed, reload posts
      if (stillActive.length === 0 && activeJobs.length > 0) {
        loadPosts();
      }
    }, POLLING_INTERVAL);

    return () => clearInterval(interval);
  }, [activeJobs, t]);

  // Load posts on mount
  useEffect(() => {
    loadPosts();
  }, []);

  const loadPosts = async () => {
    setIsLoading(true);
    try {
      // Fetch posts for each status
      const [draftRes, reviewRes, approvedRes] = await Promise.all([
        listPosts({ status: 'draft', limit: 50, sortBy: 'createdAt', sortOrder: 'desc' }),
        listPosts({ status: 'in_review', limit: 50, sortBy: 'createdAt', sortOrder: 'desc' }),
        listPosts({ status: 'approved', limit: 50, sortBy: 'createdAt', sortOrder: 'desc' }),
      ]);

      console.log('API Response - Draft:', draftRes);
      console.log('API Response - Review:', reviewRes);
      console.log('API Response - Approved:', approvedRes);

      setColumns([
        { id: 'draft', title: t('approve.columns.draft'), posts: draftRes?.posts || [] },
        { id: 'in_review', title: t('approve.columns.inReview'), posts: reviewRes?.posts || [] },
        { id: 'approved', title: t('approve.columns.approved'), posts: approvedRes?.posts || [] },
      ]);
    } catch (error) {
      console.error('Failed to load posts:', error);
      toast.error(t('approve.errors.loadFailed'));

      // Set empty columns on error
      setColumns([
        { id: 'draft', title: t('approve.columns.draft'), posts: [] },
        { id: 'in_review', title: t('approve.columns.inReview'), posts: [] },
        { id: 'approved', title: t('approve.columns.approved'), posts: [] },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  // Move post to different column
  const movePost = async (post: PostWithRelations, newStatus: KanbanColumn) => {
    if (post.post.status === newStatus) return;

    try {
      await updatePost(post.post.id, { status: newStatus });
      toast.success(t('approve.statusUpdated'));

      // Refresh posts
      loadPosts();
    } catch (error) {
      console.error('Failed to update post status:', error);
      toast.error(t('approve.errors.updateFailed'));
    }
  };

  // Cancel a running job
  const handleCancelJob = async (jobId: string) => {
    try {
      await cancelJob(jobId);
      toast.success(t('approve.jobCancelled', { defaultValue: 'Content generation cancelled' }));

      // Remove from active jobs
      setActiveJobs(prev => prev.filter(j => j.jobId !== jobId));
      updateStoredJobs(activeJobs.filter(j => j.jobId !== jobId).map(j => j.jobId));
    } catch (error) {
      console.error('Failed to cancel job:', error);
      toast.error(t('approve.errors.cancelFailed', { defaultValue: 'Failed to cancel job' }));
    }
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('nl-NL', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="h-full flex flex-col p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold dark:text-gray-100">
          {t('approve.title')}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          {t('approve.subtitle')}
        </p>
      </div>

      {/* Kanban Board */}
      <div className="flex-1 flex gap-6 overflow-x-auto pb-4">
        {isLoading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black dark:border-white mx-auto mb-4"></div>
              <p className="text-gray-600 dark:text-gray-400">{t('common.loading')}</p>
            </div>
          </div>
        ) : (
          columns.map((column) => (
            <div
              key={column.id}
              className="flex-1 min-w-[320px] max-w-[400px] flex flex-col"
            >
              {/* Column Header */}
              <div className="bg-gray-100 dark:bg-gray-800 border-2 border-black dark:border-gray-600 rounded-lg p-4 mb-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-bold dark:text-gray-100">
                    {column.title}
                  </h2>
                  <span className="bg-black dark:bg-white text-white dark:text-black px-3 py-1 rounded-full text-sm font-medium">
                    {column.posts?.length || 0}
                  </span>
                </div>
              </div>

              {/* Column Cards */}
              <div className="flex-1 space-y-4 overflow-y-auto">
                {/* Skeleton cards for active jobs (only in draft column) */}
                {column.id === 'draft' && activeJobs.map((job) => (
                  <SkeletonPostCard
                    key={job.jobId}
                    progress={job.status.progress}
                    statusMessage={job.status.statusMessage || undefined}
                    showCancelButton={true}
                    onCancel={() => handleCancelJob(job.jobId)}
                  />
                ))}

                {!column.posts || column.posts.length === 0 ? (
                  activeJobs.length === 0 && (
                    <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                      {t('approve.emptyColumn')}
                    </div>
                  )
                ) : (
                  column.posts.map((post) => {
                    const isNew = !viewedPosts.has(post.post.id);
                    return (
                    <div
                      key={post.post.id}
                      className="bg-white dark:bg-gray-800 border-2 border-black dark:border-gray-600 rounded-lg p-4 hover:shadow-brutal transition-shadow cursor-pointer relative"
                      onClick={() => {
                        setSelectedPost(post);
                        if (isNew) {
                          markPostAsViewed(post.post.id);
                        }
                      }}
                    >
                      {/* NEW Badge */}
                      {isNew && (
                        <div className="absolute -top-2 -right-2 bg-green-500 dark:bg-green-400 text-white dark:text-black px-3 py-1 rounded-full text-xs font-bold shadow-lg animate-pulse">
                          NEW
                        </div>
                      )}

                      {/* Post Title / Source Text */}
                      <div className="mb-3">
                        <h3 className="font-bold text-lg dark:text-gray-100 line-clamp-2">
                          {post.post.title || post.post.sourceText?.substring(0, 60) + '...'}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                          {formatDate(post.post.createdAt)}
                        </p>
                      </div>

                      {/* Platform Badges */}
                      <div className="flex flex-wrap gap-2 mb-3">
                        {post.variants.map((variant) => (
                          <span
                            key={variant.id}
                            className="px-2 py-1 bg-gray-100 dark:bg-gray-700 border border-black dark:border-gray-600 rounded text-xs font-medium dark:text-gray-200 capitalize"
                          >
                            {variant.platform}
                          </span>
                        ))}
                      </div>

                      {/* Token Cost */}
                      {post.post.tokenCost && (
                        <div className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                          {t('approve.tokenCost')}: {post.post.tokenCost} tokens
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="flex flex-col gap-2">
                        {/* Generate Images Button (available for all statuses) */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/${workspaceSlug}/posts/${post.post.id}/generate-images`);
                          }}
                          className="w-full px-3 py-2 border-2 border-blue-500 dark:border-blue-400 text-blue-500 dark:text-blue-400 rounded font-medium text-sm hover:bg-blue-500 hover:text-white dark:hover:bg-blue-400 dark:hover:text-black transition-all"
                        >
                          🎨 {t('imageGeneration.generate', { defaultValue: 'Generate Images' })}
                        </button>

                        {/* Status-specific actions */}
                        <div className="flex gap-2">
                        {column.id === 'draft' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              movePost(post, 'in_review');
                            }}
                            className="flex-1 px-3 py-2 bg-black dark:bg-white text-white dark:text-black border-2 border-black dark:border-white rounded font-medium text-sm hover:shadow-brutal transition-all"
                          >
                            {t('approve.actions.sendToReview')}
                          </button>
                        )}
                        {column.id === 'in_review' && (
                          <>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                movePost(post, 'approved');
                              }}
                              className="flex-1 px-3 py-2 bg-black dark:bg-white text-white dark:text-black border-2 border-black dark:border-white rounded font-medium text-sm hover:shadow-brutal transition-all"
                            >
                              {t('approve.actions.approve')}
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                movePost(post, 'draft');
                              }}
                              className="px-3 py-2 border-2 border-black dark:border-gray-600 rounded font-medium text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors dark:text-gray-100"
                            >
                              {t('approve.actions.backToDraft')}
                            </button>
                          </>
                        )}
                        {column.id === 'approved' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              // TODO: Navigate to calendar page for scheduling
                              toast.info(t('approve.scheduleComingSoon'));
                            }}
                            className="flex-1 px-3 py-2 bg-black dark:bg-white text-white dark:text-black border-2 border-black dark:border-white rounded font-medium text-sm hover:shadow-brutal transition-all"
                          >
                            {t('approve.actions.schedule')}
                          </button>
                        )}
                        </div>
                      </div>
                    </div>
                    );
                  })
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Post Detail Modal (TODO: Implement) */}
      {selectedPost && (
        <div
          className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center p-4 z-50"
          onClick={() => setSelectedPost(null)}
        >
          <div
            className="bg-white dark:bg-gray-800 border-3 border-black dark:border-gray-600 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-6">
              <h2 className="text-2xl font-bold dark:text-gray-100">
                {selectedPost.post.title || t('approve.postDetails')}
              </h2>
              <button
                onClick={() => setSelectedPost(null)}
                className="w-10 h-10 rounded-full border-2 border-black dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-center"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Source Text */}
            <div className="mb-6">
              <h3 className="font-bold mb-2 dark:text-gray-100">{t('approve.sourceText')}</h3>
              <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                {selectedPost.post.sourceText}
              </p>
            </div>

            {/* Platform Variants */}
            <div className="space-y-4">
              <h3 className="font-bold dark:text-gray-100">{t('approve.platformVariants')}</h3>
              {selectedPost.variants.map((variant) => (
                <div
                  key={variant.id}
                  className="border-2 border-black dark:border-gray-600 rounded-lg p-4"
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-bold capitalize dark:text-gray-100">
                      {variant.platform}
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {variant.characterCount} {t('approve.characters')}
                    </span>
                  </div>
                  <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap mb-3">
                    {variant.caption}
                  </p>
                  {variant.hashtags && variant.hashtags.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {variant.hashtags.map((tag, i) => (
                        <span
                          key={i}
                          className="text-sm text-blue-600 dark:text-blue-400"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
